import React, { useMemo, useState } from 'react';
import { Box, Chip, Menu, MenuItem, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { useBoardStore } from '../store/useBoardStore';
import { getCanvasMessages } from '../utils/canvasI18n';
import { normalizeStepTitle, sortSteps } from '../utils/steps';

/**
 * Current step toward the board goal. On an editable board this is a dropdown
 * of the other open steps; picking one moves the pointer (and fills that
 * step's chips if it does not have a pack yet). View-only stays read-only.
 */
function openStepOptions(steps, currentStepId) {
  const open = sortSteps(
    (Array.isArray(steps) ? steps : []).filter(
      step => step && step.status === 'open' && !step.archivedAt && normalizeStepTitle(step.title)
    )
  );
  return open.map((step, index) => ({
    id: step.id,
    title: normalizeStepTitle(step.title),
    displayPriority: index + 1,
    current: step.id === currentStepId,
  }));
}

const BoardCurrentStep = ({
  title: rawTitle = '',
  steps = [],
  currentStepId = '',
  achievementRate = null,
  variant = 'toolbar',
}) => {
  const setCurrentStep = useBoardStore(state => state.setCurrentStep);
  const isOwner = useBoardStore(state => state.isOwner);
  const canEdit = useBoardStore(state => state.canEdit);
  const { geminiLanguage } = useGeminiLanguage();
  const messages = getCanvasMessages(geminiLanguage);
  const title = normalizeStepTitle(rawTitle);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pending, setPending] = useState(false);
  const options = useMemo(
    () => openStepOptions(steps, currentStepId),
    [steps, currentStepId]
  );
  const displayPriority = useMemo(() => {
    const current = options.find(option => option.current);
    return current ? current.displayPriority : null;
  }, [options]);
  const editable = variant !== 'overlay' && (isOwner || canEdit) && options.length > 0;

  const rate =
    achievementRate == null || !Number.isFinite(Number(achievementRate))
      ? null
      : Math.max(0, Math.min(100, Math.round(Number(achievementRate))));

  const pickStep = async (option) => {
    setAnchorEl(null);
    if (!option?.id || option.id === currentStepId || pending) return;
    setPending(true);
    try {
      await setCurrentStep({ stepId: option.id });
    } finally {
      setPending(false);
    }
  };

  if (!title) return null;

  const label = (
    <>
      {displayPriority != null ? (
        <Typography
          component="span"
          className="board-current-step-priority"
          aria-hidden="true"
        >
          {displayPriority}
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary" className="board-current-step-title" component="span">
        {title}
      </Typography>
    </>
  );

  return (
    <Box className={`board-current-step board-current-step--${variant === 'overlay' ? 'overlay' : 'toolbar'}`}>
      <Box className="board-current-step-row">
        {editable ? (
          <button
            type="button"
            className="board-current-step-trigger"
            aria-haspopup="listbox"
            aria-expanded={Boolean(anchorEl)}
            aria-label={messages.selectCurrentStep}
            disabled={pending}
            onClick={event => setAnchorEl(event.currentTarget)}
          >
            {label}
            <ExpandMoreIcon className="board-current-step-trigger-chevron" fontSize="small" />
          </button>
        ) : (
          label
        )}
        {rate != null ? (
          <Chip
            size="small"
            label={`${rate}%`}
            title={messages.goalProgressTitle(rate)}
            className="board-current-step-rate"
            color={rate >= 70 ? 'success' : rate >= 35 ? 'warning' : 'default'}
            variant="outlined"
          />
        ) : null}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ 'aria-label': messages.selectCurrentStep, role: 'listbox' }}
        PaperProps={{
          className: 'board-current-step-menu-paper',
          style: { minWidth: Math.max(anchorEl?.offsetWidth || 0, 280) },
        }}
      >
        {options.map(option => (
          <MenuItem
            key={option.id}
            selected={option.current}
            role="option"
            aria-selected={option.current}
            onClick={() => { void pickStep(option); }}
          >
            <Box className="board-current-step-menu-item">
              <Typography
                component="span"
                className="board-current-step-priority"
                aria-hidden="true"
              >
                {option.displayPriority}
              </Typography>
              <Typography variant="body2" component="span" className="board-current-step-menu-title">
                {option.title}
              </Typography>
              {option.current ? (
                <CheckIcon className="board-current-step-menu-check" fontSize="small" />
              ) : null}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default BoardCurrentStep;
